
import React, { useState } from 'react';
import {
    ArrowLeft,
    Search,
    ChevronRight,
    ShieldCheck,
    Zap,
    Star,
    RefreshCw,
    AlertCircle,
    Clock,
    CheckCircle2,
    XCircle,
    ArrowRightLeft
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/shared/lib/utils';
import { useUserAssets } from '@/shared/hooks/useUserAssets';
import { useP2P, P2PListing } from '@/shared/hooks/useP2P';
import { useAuth } from '@/shared/hooks/useAuth';
import { useKYC } from '@/shared/hooks/useKYC';

const P2PPage: React.FC = () => {
    const { assets } = useUserAssets();
    const { user } = useAuth();
    const { status: kycStatus, loading: kycLoading } = useKYC(user?.uid);
    const { listings, myTrades, executeTrade, addAd, loading: p2pLoading } = useP2P();
    const navigate = useNavigate();

    // Trade Modal State
    const [activeTab, setActiveTab] = useState('buy');
    const [selectedCoin, setSelectedCoin] = useState('USDT');
    const [selectedCountry, setSelectedCountry] = useState('KR');
    const [isTradeModalOpen, setIsTradeModalOpen] = useState(false);
    const [selectedListing, setSelectedListing] = useState<P2PListing | null>(null);
    const [tradeAmountKrw, setTradeAmountKrw] = useState('');
    const [isExecuting, setIsExecuting] = useState(false);
    const [showKYCWarning, setShowKYCWarning] = useState(false);

    // Post Ad Modal State
    const [isPostModalOpen, setIsPostModalOpen] = useState(false);
    const [postData, setPostData] = useState({
        type: 'SELL' as 'BUY' | 'SELL',
        asset: 'USDT',
        price: '',
        available: '',
        limitMin: '',
        limitMax: ''
    });

    const handleOpenTrade = (listing: P2PListing) => {
        setSelectedListing(listing);
        setTradeAmountKrw('');
        setIsTradeModalOpen(true);
    };

    const handleExecuteTrade = async () => {
        // Check KYC level before allowing trade
        if (!kycStatus || kycStatus.level < 3) {
            setShowKYCWarning(true);
            return;
        }

        if (!selectedListing || !tradeAmountKrw) return;
        const amount = parseFloat(tradeAmountKrw);

        if (amount < selectedListing.limitMin || amount > selectedListing.limitMax) {
            alert(`거래 범위(₩${selectedListing.limitMin.toLocaleString()} ~ ₩${selectedListing.limitMax.toLocaleString()})를 확인해 주세요.`);
            return;
        }

        const amountCoin = amount / selectedListing.price;

        if (!confirm(`거래를 시작하시겠습니까?\n거래 금액: ₩${amount.toLocaleString()}\n예상 수량: ${amountCoin.toFixed(4)} ${selectedCoin}`)) return;

        setIsExecuting(true);
        const result = await executeTrade(selectedListing, amount, amountCoin);
        setIsExecuting(false);

        if (result.success) {
            alert('거래가 시작되었습니다. 판매자의 입금 확인을 기다려 주세요.');
            setIsTradeModalOpen(false);
            setActiveTab('myTrades');
        } else {
            alert(`거래 중 오류가 발생했습니다: ${result.error}`);
        }
    };

    const handlePostAd = async () => {
        if (!postData.price || !postData.available || !postData.limitMin || !postData.limitMax) {
            alert('모든 필드를 입력해 주세요.');
            return;
        }

        setIsExecuting(true);
        try {
            await addAd({
                type: postData.type,
                asset: postData.asset,
                price: parseFloat(postData.price),
                available: parseFloat(postData.available),
                limitMin: parseFloat(postData.limitMin),
                limitMax: parseFloat(postData.limitMax),
                advertiserName: '내 프로필', // 실제 서비스에서는 유저 닉네임을 사용
                rating: 5.0,
                trades: 0,
                status: 'Active'
            });
            alert('광고가 성공적으로 등록되어 마켓에 노출됩니다.');
            setIsPostModalOpen(false);
        } catch (e: any) {
            alert('등록 중 오류 발생: ' + e.message);
        }
        setIsExecuting(false);
    };

    if (p2pLoading || kycLoading) {
        return <div className="min-h-screen flex items-center justify-center">
            <Zap className="h-12 w-12 text-blue-500 animate-spin" />
        </div>;
    }


    return (
        <div className="min-h-screen pb-24 text-foreground animate-in fade-in duration-1000">
            <div className="max-w-7xl mx-auto px-4 lg:px-8 space-y-12">

                {/* Header Section */}
                <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-10 pt-8">
                    <div className="space-y-4 max-w-2xl">
                        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-blue-500">
                            <ArrowRightLeft className="h-3 w-3 fill-blue-500" />
                            <span className="text-[9px] font-black uppercase tracking-[0.2em]">Institutional Escrow Marketplace</span>
                        </div>
                        <h1 className="text-4xl md:text-5xl font-black tracking-tighter leading-[0.9] uppercase">
                            신뢰할 수 있는 사용자 간<br />
                            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 dark:from-blue-500 dark:via-indigo-500 dark:to-violet-500">
                                P2P 다이렉트 뱅킹
                            </span>
                        </h1>
                        <p className="text-muted-foreground font-bold text-sm md:text-base leading-relaxed max-w-lg uppercase tracking-tight">
                            검증된 트레이더들과 실시간으로 자산을 교환하세요.<br />
                            모든 거래는 시스템 에스크로를 통해 100% 안전하게 보호됩니다.
                        </p>
                    </div>

                    <button
                        onClick={() => setIsPostModalOpen(true)}
                        className="h-16 px-10 rounded-[1.5rem] bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-black text-[11px] uppercase tracking-[0.2em] shadow-xl hover:scale-[1.02] active:scale-95 transition-all flex items-center gap-3 group/btn">
                        <Zap className="h-4 w-4 fill-white" />
                        <span>게시글 등록 (Post Ad)</span>
                        <ChevronRight className="h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                    </button>
                </div>

                {/* Filter & Control Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-end bg-accent/30 p-8 rounded-[2.5rem] border border-border transition-colors">
                    <div className="space-y-3">
                        <label className="text-[10px] font-black text-muted-foreground uppercase tracking-[0.2em] px-1 italic">Currency & Region</label>
                        <select
                            value={selectedCountry}
                            onChange={(e) => setSelectedCountry(e.target.value)}
                            className="w-full h-14 bg-card border border-border rounded-2xl px-6 text-sm font-black outline-none focus:border-blue-500/50 transition-all text-foreground shadow-inner"
                        >
                            <option value="KR" className="bg-card">South Korea (KRW)</option>
                            <option value="US" className="bg-card">United States (USD)</option>
                            <option value="CN" className="bg-card">China (CNY)</option>
                        </select>
                    </div>
                    <div className="space-y-3">
                        <label className="text-[10px] font-black text-muted-foreground uppercase tracking-[0.2em] px-1 italic">Target Asset Asset</label>
                        <div className="flex gap-2 p-1.5 bg-card rounded-2xl border border-border h-14 shadow-inner">
                            {['USDT', 'TRX', 'BTC', 'ETH'].map(coin => (
                                <button
                                    key={coin}
                                    onClick={() => setSelectedCoin(coin)}
                                    className={cn(
                                        "flex-1 rounded-xl text-[10px] font-black transition-all uppercase tracking-widest",
                                        selectedCoin === coin ? "bg-blue-600 text-white shadow-lg" : "text-muted-foreground hover:text-foreground"
                                    )}
                                >
                                    {coin}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="space-y-3">
                        <label className="text-[10px] font-black text-muted-foreground uppercase tracking-[0.2em] px-1 italic">Action Mode</label>
                        <div className="flex p-1.5 bg-card rounded-2xl border border-border h-14 shadow-inner">
                            <button onClick={() => setActiveTab('buy')} className={cn("flex-1 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all", activeTab === 'buy' ? "bg-emerald-600 text-white shadow-lg" : "text-muted-foreground hover:text-foreground")}>구매 (BUY)</button>
                            <button onClick={() => setActiveTab('sell')} className={cn("flex-1 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all", activeTab === 'sell' ? "bg-rose-600 text-white shadow-lg" : "text-muted-foreground hover:text-foreground")}>판매 (SELL)</button>
                            <button onClick={() => setActiveTab('myTrades')} className={cn("flex-1 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all", activeTab === 'myTrades' ? "bg-blue-600 text-white shadow-lg" : "text-muted-foreground hover:text-foreground")}>기록 (MY)</button>
                        </div>
                    </div>
                </div>

                {/* Verification Guard Notice */}
                <div className="group relative p-8 rounded-[2.5rem] bg-card border border-border overflow-hidden transition-all hover:border-amber-500/20 shadow-2xl">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/[0.03] blur-3xl rounded-full"></div>
                    <div className="relative z-10 flex flex-col md:flex-row items-center gap-8">
                        <div className="w-16 h-16 rounded-2xl bg-amber-500/10 flex items-center justify-center shrink-0 border border-amber-500/20">
                            <AlertCircle className="h-8 w-8 text-amber-600 dark:text-amber-500" />
                        </div>
                        <div className="flex-1 space-y-2 text-center md:text-left">
                            <h4 className="text-[11px] font-black text-amber-600 dark:text-amber-500 uppercase tracking-[0.2em]">Institutional Compliance Protocol</h4>
                            <p className="text-xs font-bold text-muted-foreground leading-relaxed uppercase tracking-tight italic">
                                P2P 거래를 이용하시려면 [고급인증] 이상의 보안 단계가 필요합니다. 거래 시작 전 KYC 인증 센터에서 신분증 및 계좌 인증을 완료해 주시기 바랍니다.
                            </p>
                        </div>
                        <button
                            onClick={() => navigate('/verification')}
                            className="px-8 h-12 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-amber-500 text-[10px] font-black uppercase tracking-widest transition-all hover:bg-amber-500/20">보안센터 이동</button>
                    </div>
                </div>

                {/* Listings Grid */}
                <div className="space-y-8">
                    {activeTab === 'myTrades' ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {myTrades.map(trade => (
                                <div key={trade.id} className="p-8 rounded-[2.5rem] bg-card border border-border hover:border-blue-500/30 transition-all flex items-center justify-between group shadow-xl relative overflow-hidden">
                                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/[0.02] to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                                    <div className="flex items-center gap-6 relative z-10">
                                        <div className={cn("w-14 h-14 rounded-2xl flex items-center justify-center font-black border group-hover:scale-110 transition-transform duration-500",
                                            trade.type === 'BUY' ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20' : 'bg-rose-500/10 text-rose-600 border-rose-500/20')}>
                                            {trade.type}
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-3">
                                                <h3 className="font-black text-foreground text-base tracking-tight uppercase">{trade.advertiserName}</h3>
                                                <span className="text-[9px] font-black text-muted-foreground uppercase opacity-60 tabular-nums">{trade.createdAt?.toDate ? trade.createdAt.toDate().toLocaleString() : 'Processing...'}</span>
                                            </div>
                                            <p className="text-lg font-black text-foreground mt-1 tabular-nums italic">
                                                {trade.totalCoin.toLocaleString()} <span className="text-[10px] text-muted-foreground">{trade.asset}</span>
                                                <span className="mx-2 text-muted-foreground/30">•</span>
                                                <span className="text-blue-600 dark:text-blue-500 text-sm">₩{trade.totalKrw.toLocaleString()}</span>
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-4 relative z-10">
                                        <div className={cn(
                                            "px-4 py-1.5 rounded-full text-[9px] font-black uppercase tracking-[0.2em] border shadow-sm",
                                            trade.status === 'COMPLETED' ? "bg-accent text-muted-foreground border-border" : "bg-blue-500/10 text-blue-600 dark:text-blue-500 border-blue-500/20 animate-pulse"
                                        )}>
                                            {trade.status}
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {myTrades.length === 0 && (
                                <div className="col-span-full py-20 text-center text-muted-foreground font-black uppercase tracking-widest italic opacity-50">
                                    참여 중인 거래 내역이 없습니다.
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {listings
                                .filter(l => l.asset === selectedCoin)
                                .filter(l => l.type === (activeTab === 'buy' ? 'SELL' : 'BUY'))
                                .map(list => (
                                    <div key={list.id} className="p-10 rounded-[3rem] bg-card border border-border hover:border-blue-500/30 transition-all duration-500 group flex flex-col lg:flex-row lg:items-center justify-between gap-12 shadow-2xl relative overflow-hidden">
                                        <div className="absolute top-0 right-0 w-80 h-80 bg-blue-500/[0.01] blur-3xl -mr-40 -mt-40 rounded-full group-hover:bg-blue-500/[0.03] transition-colors duration-1000"></div>

                                        <div className="flex items-center gap-8 relative z-10 min-w-[280px]">
                                            <div className="w-20 h-20 rounded-[2rem] bg-accent flex items-center justify-center border border-border shadow-xl group-hover:scale-105 group-hover:shadow-blue-500/10 transition-all duration-700 overflow-hidden relative">
                                                <div className="absolute inset-x-0 bottom-0 h-1 bg-blue-500"></div>
                                                <span className="text-3xl font-black text-blue-600 dark:text-blue-500 italic uppercase">{list.advertiserName[0]}</span>
                                            </div>
                                            <div className="space-y-2">
                                                <div className="flex items-center gap-3">
                                                    <h4 className="font-black text-foreground text-xl tracking-tight">{list.advertiserName}</h4>
                                                    <div className="p-1 rounded bg-blue-500/10">
                                                        <CheckCircle2 className="h-3.5 w-3.5 text-blue-500" />
                                                    </div>
                                                </div>
                                                <div className="flex flex-wrap items-center gap-4">
                                                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-amber-500/10 text-amber-600">
                                                        <Star className="h-3.5 w-3.5 fill-amber-500" />
                                                        <span className="text-[11px] font-black tabular-nums">{list.rating}</span>
                                                    </div>
                                                    <span className="text-[10px] font-black text-muted-foreground uppercase tracking-widest italic opacity-60">
                                                        {list.trades} TRADES <span className="mx-1">/</span> 99% RATIO
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 md:grid-cols-3 flex-1 gap-10 px-0 lg:px-12 border-border lg:border-x relative z-10">
                                            <div className="space-y-1">
                                                <p className="text-[10px] font-black text-muted-foreground uppercase tracking-[0.3em] mb-2 italic">Liquid Available</p>
                                                <p className="text-lg font-black text-foreground uppercase tracking-tight tabular-nums">
                                                    {list.available.toLocaleString()} <span className="text-[10px] text-muted-foreground opacity-60">{selectedCoin}</span>
                                                </p>
                                            </div>
                                            <div className="space-y-1">
                                                <p className="text-[10px] font-black text-muted-foreground uppercase tracking-[0.3em] mb-2 italic">Transactional Bounds</p>
                                                <p className="text-sm font-black text-foreground tabular-nums opacity-80 italic">
                                                    ₩{list.limitMin.toLocaleString()} <span className="mx-1 text-muted-foreground/30">~</span> ₩{list.limitMax.toLocaleString()}
                                                </p>
                                            </div>
                                            <div className="col-span-2 md:col-span-1 space-y-1">
                                                <p className="text-[10px] font-black text-muted-foreground uppercase tracking-[0.3em] mb-2 italic">Settlement Price</p>
                                                <div className="flex items-baseline gap-2">
                                                    <span className="text-3xl font-black text-blue-600 dark:text-blue-500 font-mono tracking-tighter tabular-nums">₩{list.price.toLocaleString()}</span>
                                                    <span className="text-[10px] font-black text-muted-foreground uppercase">KRW</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="shrink-0 relative z-10">
                                            <button
                                                onClick={() => handleOpenTrade(list)}
                                                className={cn(
                                                    "w-full lg:w-56 h-16 rounded-2xl text-white font-black text-[11px] uppercase tracking-[0.2em] shadow-2xl transition-all hover:scale-[1.03] active:scale-95 flex items-center justify-center gap-3 group/btn",
                                                    activeTab === 'buy' ? 'bg-emerald-600 shadow-emerald-500/20' : 'bg-rose-600 shadow-rose-500/20'
                                                )}>
                                                <span>{activeTab === 'buy' ? 'ASSET BUY' : 'ASSET SELL'}</span>
                                                <ArrowRightLeft className="h-4 w-4 group-hover/btn:rotate-180 transition-transform duration-700" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                        </div>
                    )}
                </div>

                {/* ESCROW ASSURANCE */}
                <div className="relative p-10 rounded-[3rem] bg-accent/30 border border-border flex flex-col md:flex-row items-center gap-10 overflow-hidden group">
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-600 to-indigo-600 opacity-20"></div>
                    <div className="w-20 h-20 rounded-[2rem] bg-blue-500/10 flex items-center justify-center shrink-0 border border-blue-500/20 shadow-xl group-hover:scale-110 transition-transform duration-700">
                        <ShieldCheck className="h-10 w-10 text-blue-600 dark:text-blue-500" />
                    </div>
                    <div className="space-y-3 flex-1 text-center md:text-left">
                        <h4 className="font-black text-blue-600 dark:text-blue-500 text-lg uppercase tracking-widest italic leading-none">cointocash Proprietary Escrow Vault</h4>
                        <p className="text-xs text-muted-foreground font-black leading-relaxed uppercase tracking-tight italic">
                            모든 P2P 거래는 코인투캐쉬의 에스크로 시스템을 통해 보호됩니다. 판매자가 구매자로부터 입금을 확인하기 전까지 자산은 안전하게 락업되며, 입금 확인 후 즉시 구매자에게 전송됩니다. 거래 중 문제가 발생하면 24/7 고객센터를 통해 중재를 요청할 수 있습니다.
                        </p>
                    </div>
                    <button className="px-8 h-14 rounded-2xl bg-foreground text-background text-[10px] font-black uppercase tracking-[0.2em] shadow-xl hover:scale-[1.02] active:scale-95 transition-all">
                        Rulebook View
                    </button>
                </div>
            </div>

            {/* Trade Execution Modal (Buyer/Seller Match) */}
            {isTradeModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-background/80 backdrop-blur-xl animate-in fade-in duration-500">
                    <div className="w-full max-w-lg bg-card border border-border rounded-[3rem] p-10 space-y-10 shadow-2xl relative overflow-hidden transition-all animate-in zoom-in-95 duration-500">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/[0.05] blur-3xl rounded-full -mr-32 -mt-32"></div>

                        <div className="flex justify-between items-center relative z-10">
                            <div className="space-y-1">
                                <h2 className="text-3xl font-black text-foreground uppercase tracking-tighter leading-none">{activeTab === 'buy' ? 'Trade Execution' : 'Asset Liquidation'}</h2>
                                <p className="text-[10px] font-black text-muted-foreground uppercase tracking-[0.3em] italic">Instant Escrow Matching Order</p>
                            </div>
                            <button onClick={() => setIsTradeModalOpen(false)} className="w-10 h-10 rounded-full bg-accent border border-border flex items-center justify-center text-muted-foreground hover:text-foreground transition-all">
                                <span className="font-black text-xs leading-none">×</span>
                            </button>
                        </div>

                        <div className="p-8 rounded-[2rem] bg-accent/50 border border-border space-y-4 relative z-10">
                            <div className="flex items-center justify-between">
                                <span className="text-[10px] font-black text-muted-foreground uppercase tracking-widest">Master Advertiser</span>
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-black text-foreground uppercase">{selectedListing?.advertiserName}</span>
                                    <CheckCircle2 className="h-3.5 w-3.5 text-blue-500" />
                                </div>
                            </div>
                            <div className="w-full h-px bg-border/50"></div>
                            <div className="flex items-center justify-between">
                                <span className="text-[10px] font-black text-muted-foreground uppercase tracking-widest">Market Quotation</span>
                                <span className="text-xl font-black text-blue-600 dark:text-blue-500 tabular-nums font-mono">₩{selectedListing?.price.toLocaleString()}</span>
                            </div>
                        </div>

                        <div className="space-y-6 relative z-10">
                            <div className="space-y-3">
                                <div className="flex items-center justify-between px-1">
                                    <label className="text-[10px] font-black text-muted-foreground uppercase tracking-[0.2em] italic">Amount to Settle (KRW)</label>
                                    <span className="text-[9px] font-black text-blue-500 italic uppercase">Min Limit: ₩{selectedListing?.limitMin.toLocaleString()}</span>
                                </div>
                                <div className="relative group">
                                    <input
                                        type="number"
                                        value={tradeAmountKrw}
                                        onChange={(e) => setTradeAmountKrw(e.target.value)}
                                        placeholder="TOTAL KRW VOLUME"
                                        className="w-full h-16 bg-accent/30 border border-border rounded-2xl px-6 text-xl font-black outline-none focus:border-blue-500/50 text-foreground transition-all shadow-inner"
                                    />
                                    <span className="absolute right-6 top-1/2 -translate-y-1/2 text-xs font-black text-muted-foreground uppercase tracking-widest">KRW</span>
                                </div>
                            </div>
                            <div className="space-y-3">
                                <label className="text-[10px] font-black text-muted-foreground uppercase tracking-[0.2em] italic px-1">Projected Fulfillment ({selectedCoin})</label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        disabled
                                        value={(parseFloat(tradeAmountKrw || '0') / (selectedListing?.price || 1)).toFixed(4)}
                                        className="w-full h-16 bg-accent border border-border rounded-2xl px-6 text-xl font-black text-muted-foreground cursor-not-allowed opacity-60"
                                    />
                                    <span className="absolute right-6 top-1/2 -translate-y-1/2 text-xs font-black text-muted-foreground uppercase tracking-widest">{selectedCoin}</span>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-6 relative z-10">
                            <button
                                onClick={handleExecuteTrade}
                                disabled={isExecuting || !tradeAmountKrw}
                                className="w-full h-20 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-black text-xs uppercase tracking-[0.3em] hover:scale-[1.02] active:scale-95 transition-all shadow-2xl shadow-blue-600/20 flex items-center justify-center gap-4 group/submit disabled:opacity-50">
                                <span>{isExecuting ? 'PROCESSING...' : 'Initiate Smart Contract'}</span>
                                <Zap className="h-5 w-5 fill-white group-hover/submit:scale-125 transition-transform" />
                            </button>
                            <div className="flex items-center justify-center gap-2 opacity-60 italic">
                                <ShieldCheck className="h-3 w-3 text-blue-500" />
                                <p className="text-[9px] font-black text-muted-foreground uppercase tracking-widest text-center">Protected By cointocash Institutional-Grade Escrow</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Post Ad Modal (User Create New Ad) */}
            {isPostModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-background/80 backdrop-blur-xl animate-in fade-in duration-500">
                    <div className="w-full max-w-2xl bg-card border border-border rounded-[3rem] p-10 space-y-8 shadow-2xl relative overflow-hidden transition-all animate-in slide-in-from-bottom-8 duration-500">
                        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-blue-600 to-indigo-600"></div>

                        <div className="flex justify-between items-center relative z-10">
                            <div className="space-y-1">
                                <h2 className="text-3xl font-black text-foreground uppercase tracking-tighter leading-none">Market Ad Registration</h2>
                                <p className="text-[10px] font-black text-muted-foreground uppercase tracking-[0.3em] italic">Post Your Assets in Global P2P Liquidity</p>
                            </div>
                            <button onClick={() => setIsPostModalOpen(false)} className="w-10 h-10 rounded-full bg-accent border border-border flex items-center justify-center text-muted-foreground hover:text-foreground transition-all">
                                <span className="font-black text-xs leading-none">×</span>
                            </button>
                        </div>

                        <div className="grid grid-cols-2 gap-6 relative z-10">
                            <div className="space-y-3">
                                <label className="text-[10px] font-black text-muted-foreground uppercase tracking-[0.2em] italic px-1">Ad Type</label>
                                <div className="flex p-1.5 bg-accent rounded-2xl border border-border h-14">
                                    <button
                                        onClick={() => setPostData({ ...postData, type: 'SELL' })}
                                        className={cn("flex-1 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all", postData.type === 'SELL' ? "bg-rose-600 text-white" : "text-muted-foreground")}>판매 (Sell Ad)</button>
                                    <button
                                        onClick={() => setPostData({ ...postData, type: 'BUY' })}
                                        className={cn("flex-1 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all", postData.type === 'BUY' ? "bg-emerald-600 text-white" : "text-muted-foreground")}>구매 (Buy Ad)</button>
                                </div>
                            </div>
                            <div className="space-y-3">
                                <label className="text-[10px] font-black text-muted-foreground uppercase tracking-[0.2em] italic px-1">Asset Asset</label>
                                <select
                                    value={postData.asset}
                                    onChange={(e) => setPostData({ ...postData, asset: e.target.value })}
                                    className="w-full h-14 bg-accent border border-border rounded-2xl px-6 text-sm font-black outline-none focus:border-blue-500/50">
                                    <option value="USDT">USDT</option>
                                    <option value="TRX">TRX</option>
                                    <option value="BTC">BTC</option>
                                    <option value="ETH">ETH</option>
                                </select>
                            </div>
                        </div>

                        <div className="space-y-6 relative z-10">
                            <div className="space-y-3">
                                <label className="text-[10px] font-black text-muted-foreground uppercase tracking-[0.2em] italic px-1">Ad Price (₩ / Unit)</label>
                                <div className="relative group">
                                    <input
                                        type="number"
                                        value={postData.price}
                                        onChange={(e) => setPostData({ ...postData, price: e.target.value })}
                                        placeholder="SET UNIT PRICE"
                                        className="w-full h-16 bg-accent/30 border border-border rounded-2xl px-6 text-xl font-black outline-none focus:border-blue-500/50 text-foreground transition-all shadow-inner"
                                    />
                                    <span className="absolute right-6 top-1/2 -translate-y-1/2 text-[10px] font-black text-muted-foreground uppercase tracking-widest italic opacity-60">KRW / {postData.asset}</span>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-6">
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-muted-foreground uppercase tracking-[0.2em] italic px-1">Total Limit Liquidity</label>
                                    <div className="relative">
                                        <input
                                            type="number"
                                            value={postData.available}
                                            onChange={(e) => setPostData({ ...postData, available: e.target.value })}
                                            placeholder="AVAILABLE SUM"
                                            className="w-full h-14 bg-accent border border-border rounded-2xl px-6 text-base font-black outline-none focus:border-blue-500/50"
                                        />
                                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[10px] font-black text-muted-foreground">{postData.asset}</span>
                                    </div>
                                </div>
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-muted-foreground uppercase tracking-[0.2em] italic px-1">Risk Threshold Bounds</label>
                                    <div className="flex gap-2">
                                        <input
                                            type="number"
                                            value={postData.limitMin}
                                            onChange={(e) => setPostData({ ...postData, limitMin: e.target.value })}
                                            placeholder="MIN ₩"
                                            className="w-full h-14 bg-accent border border-border rounded-2xl px-4 text-xs font-black outline-none focus:border-blue-500/50"
                                        />
                                        <input
                                            type="number"
                                            value={postData.limitMax}
                                            onChange={(e) => setPostData({ ...postData, limitMax: e.target.value })}
                                            placeholder="MAX ₩"
                                            className="w-full h-14 bg-accent border border-border rounded-2xl px-4 text-xs font-black outline-none focus:border-blue-500/50"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="p-6 rounded-[1.5rem] bg-blue-500/5 border border-blue-500/10 space-y-2">
                            <div className="flex items-center gap-2">
                                <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-500" />
                                <span className="text-[10px] font-black text-blue-600 dark:text-blue-500 uppercase tracking-widest">Platform Escrow Warning</span>
                            </div>
                            <p className="text-[9px] font-bold text-muted-foreground leading-relaxed uppercase tracking-tight italic">
                                광고 게시 시 에스크로 계약을 위해 즉시 자산의 노출이 허용됩니다. 구매자와의 분쟁 발생 시 플랫폼 중재가 가능하도록 반드시 정확한 본인 명의 계좌를 설정해 주시기 바랍니다.
                            </p>
                        </div>

                        <button
                            onClick={handlePostAd}
                            disabled={isExecuting}
                            className="w-full h-20 rounded-2xl bg-foreground text-background font-black text-xs uppercase tracking-[0.3em] hover:scale-[1.02] active:scale-95 transition-all shadow-2xl flex items-center justify-center gap-4 group/submit disabled:opacity-50">
                            <span>{isExecuting ? 'UPLOADING...' : 'Broadcast Assets to Market'}</span>
                            <ArrowRightLeft className="h-5 w-5 group-hover/submit:rotate-180 transition-transform duration-700" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default P2PPage;
